/*

  e y e k a n d y  a p p  c o r e
  Eyekandy App Core

  :description:
  A file to create a "core" for any JS application destined for deployment into a browser environment.

  Allows us to bind together all of our subpackages into a single JS import.

*/

//
//  :code:
import { talk } from '../js/talk'
import { EyekandyBrowserInterface } from './EyekandyBrowserInterface'
import { EyekandyEventsInterface } from './EyekandyEventsInterface'
import { EyekandyEventLoggingInterface } from './EyekandyEventLoggingInterface'
import { EyekandyLocalisationInterface } from './EyekandyLocalisationInterface'

//
//  :packages:
import { Environment } from '@eyekandy/eky-environment'
import { AndroidARLaunch, iOSARLaunch, QRARLaunch } from '@eyekandy/ar-launch'
import { EyekandyDistributorConfigsInterface } from './EyekandyDistributorConfigsInterface'
import { EyekandyMatchingInterface } from './EyekandyMatchingInterface'

//
//  :statics:
export const EYEKANDY_APP_CORE_WINDOW_KEY = 'EyekandyAppCore'

//
//  :classes:
export class EyekandyAppCore {
  constructor (options) {
    //
    //  :pre:
    //  Start any initial logic for input parameters here.
    if (!options) {
      this.options = {}
    } else {
      this.options = options
    }

    //
    //  :core:
    //  Core settings and values.
    this.name = 'eyekandy-app-core'
    this.version = 'v5.0.0'
    this.group = '[EYEKANDY-APP-CORE]'
    this.environment = 'dev'
    const DEFAULT_ENVIRONMENT_OPTIONS = { environment: 'dev' }
    talk([this.group, `starting to create a new instance @ '${this.version}'`])

    //
    //  :events-bus:
    //  Set up our events bus using the DOM.
    this.events = new EyekandyEventsInterface()

    //
    //  :browser:
    //  Set up attributes to hold our browser library.
    this.browser = new EyekandyBrowserInterface()

    //
    //  :environment:
    //  Set up an attribute to hold our environment library.
    this.env = new Environment()

    //
    //  :logging:
    //  To handle logging we will assemble the events outside of this class and pass them in.
    this.log = new EyekandyEventLoggingInterface()

    //
    //  :qrs:
    //  Set up attributes to handle rendering QR URLs.
    this.qr = new QRARLaunch(DEFAULT_ENVIRONMENT_OPTIONS)

    //
    //  :ar:
    //  Set up attributes to handle AR.
    this.android = new AndroidARLaunch(DEFAULT_ENVIRONMENT_OPTIONS)
    this.ios = new iOSARLaunch(DEFAULT_ENVIRONMENT_OPTIONS)
    this.ar = null // Set at runtime to either this.android || this.ios

    //
    //  :localisation:
    //  Set up attributes related to localisation (loaded as JS over the network).
    this.localisation = { loaded: false }
    this.localiser = new EyekandyLocalisationInterface({
      browser: this.browser
    })

    //
    //  :distributor-configs:
    this.configs = new EyekandyDistributorConfigsInterface(DEFAULT_ENVIRONMENT_OPTIONS)

    //
    //  :matching:
    this.matching = new EyekandyMatchingInterface(DEFAULT_ENVIRONMENT_OPTIONS)

    //
    //  :bindings:
    //  As we're a singleton, others may wish to use our location to bind functions to.
    this.bindings = {
      talk: talk,
      loadJSON: this.browser.loadJSON
    }
    this.data = {}

    //
    //  :setup-function:
    //  The functionality of this class can be extended using a setup function, define the name we expect for it.
    this.SETUP_FUNCTION_NAME = 'ekyAppCoreSetup'
  }

  //
  //  :overrides:
  //  Functions that can be overriden by external callers, must be done before calling go().
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -
  beforeOnLoad () {}
  afterOnLoad () {}

  //
  //  :api:
  //  Our primary mechanism to start this class and make it ready for use.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -
  go () {
    //
    //  :step 1:
    //  When complete we can return our initialise() running promise.
    return this.initialise()
  }

  async initialise () {
    const started = Date.now()
    //
    //  :step 0:
    //  Handle situations where window may be unavailable to us.
    if (!window) {
      return false
    }

    //
    //  :step 1:
    //  We need to run our function before the user sets up.
    this._preUserSetupFunction()

    //
    //  :step 2:
    //  Identify if the user has defined a setup function.
    if (typeof window[this.SETUP_FUNCTION_NAME] === 'function') {
      //
      //  There is a setup function defined in our context, pass ourself into it and execute.
      talk([
        this.group,
        `a setup function is defined @ window.${this.SETUP_FUNCTION_NAME}(), calling it now`
      ])
      window[this.SETUP_FUNCTION_NAME](this)
    }

    //
    //  :step 3:
    //  We need to run our function after the user sets up.
    await this._postUserSetupFunction()

    //
    //  :step 4:
    //  Run the public function for ready (callers can override it).
    await this.beforeOnLoad()

    //
    //  :step 5:
    //  Emit an event on window, the user may want to bind to that instead of a window function.
    talk([
      this.group,
      `initialise() completes in '${(Date.now() - started).toLocaleString()}ms'`
    ])
    talk([
      this.group,
      `dispatching an '${this.name}-load' event to window as we are now ready`
    ])
    this.events.dispatch(`${this.name}-load`, null, window)

    //
    //  :step 6:
    //  Run the public function for after on load (callers can override it).
    await this.afterOnLoad()

    //
    //  All done, return true as we managed to complete all of our steps.
    return true
  }

  readBrowserConfig (key) {
    return this.browser._readConfigValue(key)
  }

  setOptionValue (key, value) {
    this.options[key] = value
  }

  bindFunction (key, func) {
    this.bindings[key] = func
  }

  callBoundFunction (key, args) {
    if (typeof args === 'undefined') {
      args = []
    }
    return this.bindings[key](...args)
  }

  setData (key, value) {
    this.data[key] = value
  }
  getData (key) {
    return this.data[key]
  }

  initialiseLoggingLibraryRuntime (mode, options, customClass) {
    if (!options) {
      options = {}
    }
    const selfOptions = {
      distributor: this.readBrowserConfig('distributor') || 'eyekandy-app-core',
      language: this.readBrowserConfig('language') || 'en',
      environment: this.readBrowserConfig('environment') || 'dev',
      location: this.readBrowserConfig('page') || null,
      user: this.readBrowserConfig('user') || null,
      session: this.readBrowserConfig('') || null
    }
    const combinedOptions = {
      ...selfOptions,
      ...options
    }
    this.log.initialiseForMode(mode, combinedOptions, customClass)
  }

  //
  //  :setup:
  //  As the configuration of some of our attributes can be extended by the caller at runtime, call a known function
  //  from window that the caller will define themselves.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -
  _preUserSetupFunction () {
    //
    //  Nothing here for now.
    //
    talk([this.group, `running preUserSetupFunction()`])
    return true
  }

  async _postUserSetupFunction () {
    talk([this.group, `running postUserSetupFunction()`])
    //
    //  :step 1:
    //  We need to parse the browser arguments as the user may of defined some GET arguments to read/parse.
    this.browser.readConfigOptionsFromCurrentUrl()

    //
    //  :step 2:
    //  We may now need to "upsert" some of our values into our the interfaces attached to ourself.
    this.upsertConfigToSelfFromBrowser()

    //
    //  :step 3:
    //  Some async behaviour is required, we can at least handle things in parallel though.
    //  We might want to have a try-catch here for safety as the promises might fail unexpectedly.
    await Promise.all([
      //
      //  :step 3a:
      //  We may need to get our localisation package into memory from JSON, but only if flagged by our options.
      this._loadRemoteLocalisationsFile(),

      //
      //  :step 3b:
      //  We might need to scan for AR support, but only if flagged by our options.
      this._handleScanningForAR()
    ])

    //
    //  All done, return true.
    return true
  }

  async _loadRemoteLocalisationsFile () {
    if (this.options.localisation) {
      if (this.options.localisation.app && this.options.localisation.version) {
        await this.localiser.fetchRemoteJSONLocalisations(
          this.options.localisation.app,
          this.options.localisation.version
        )
      }
    }
  }

  async _handleScanningForAR () {
    //
    //  If we are not flagged to scan in options, do not scan.
    if (!this.options.scanForAR) {
      return false
    }
    //
    //  If a flag is set to skip the AR scan, set it on our Environment library.
    if (this.options.skipARScan) {
      this.env.setSkipARScans()
    }
    //
    //  Ok, scan our environment using Environment.
    talk([
      this.group,
      `scanning the current environment to verify if AR is available...`
    ])
    await this.env.scan()
    //
    //  At this point we may have an override enabled, if so, set it.
    if (this.readBrowserConfig('forceEnableAR')) {
      talk(
        [
          this.group,
          `caution, a flag is set to force enabling of AR functionality, expect issues`
        ],
        'warning'
      )
      this.env.canHandleAR = true
    }
    //
    //  At this point we also know what kind of AR to use.
    if (this.env.isiOS()) {
      this.ar = this.ios
    }
    if (this.env.isAndroid()) {
      this.ar = this.android
    }
    //
    //  The scan is complete, echo the results and return true.
    talk([this.group, `environment scan results:`, this.env.serialise()])
    return true
  }

  upsertConfigToSelfFromBrowser (
    environment,
    distributor,
    language,
    project,
    page,
    user,
    session
  ) {
    //
    //  :environment:
    //  For now we'll upsert environment globally.
    const environment_ = environment || this.readBrowserConfig('environment')
    if (environment_) {
      this.qr.environment = environment_
      this.android.environment = environment_
      this.ios.environment = environment_
      this.log.upsertToLibs({ environment: environment_ })
    }

    //
    //  :distributor-language:
    //  Only some of our interfaces need these values.
    const distributor_ = distributor || this.readBrowserConfig('distributor')
    if (distributor_) {
      this.log.upsertToLibs({ distributor: distributor_ })
    }
    const language_ = language || this.readBrowserConfig('language')
    if (language_) {
      this.log.upsertToLibs({ language: language_ })
      this.localiser.language = language_
    }

    //
    //  :project:
    const project_ = project || this.readBrowserConfig('project')
    if (project_) {
      this.localiser.project = project_
    }

    //
    //  :page:
    const page_ = page || this.readBrowserConfig('page')
    if (page_) {
      this.log.upsertToLibs({ location: page_ })
    }

    //
    //  :user:
    const user_ = user || this.readBrowserConfig('user')
    if (user_) {
      this.log.upsertToLibs({ user: user_ })
    }

    //
    //  :session:
    const session_ = session || this.readBrowserConfig('session')
    if (session_) {
      this.log.upsertToLibs({ session: session_ })
    }
  }
}
